import {
  HubProposer,
  InsightProposer,
  type HubProposal,
  type InsightInput,
  type InsightProposal,
  type LLMJudge,
  type Proposal,
  loadDeclinedFingerprints,
  resetDeclined,
} from "@vortex-os/proactive-curator";
import type { Command, CommandInput } from "@vortex-os/slash-commands";

/**
 * `/curate` — surface accumulated proposals from the proactive-curator
 * module. Two surfaces share one command:
 *
 *  - `/curate`                    — run both proposers; surface any results.
 *  - `/curate --insight`          — InsightProposer only.
 *  - `/curate --hub`              — HubProposer only.
 *  - `/curate --reset-declined`   — clear the decline list (escape hatch).
 *
 * Host integration:
 *  1. Construct an `LLMJudge` adapter for the host (Claude Code / Codex /
 *     Gemini / Claude Desktop) — see `@vortex-os/proactive-curator`.
 *  2. Optionally provide an `insightInputProvider` callback that returns
 *     the current `InsightInput` (recent turns + accumulated tokens) when
 *     the host wants the in-session insight surface. Without this provider
 *     the insight proposer is skipped.
 *  3. Pass both to `createRitualRegistry({ llm, insightInputProvider })`.
 *     The `/curate` command is registered only when an LLMJudge is
 *     supplied — instances that have not wired up a host LLM get the rest
 *     of the rituals without a half-broken curate command.
 *
 * Result shape carries the full `Proposal` objects (with `onAccept` and
 * `onDecline` thunks). Hosts render the previews, ask the user to decide,
 * then call the thunks. This is the one command in `session-rituals`
 * whose result is intentionally non-serializable — the thunks are the
 * point.
 */

export interface CurateOptions {
  readonly llm: LLMJudge;
  /**
   * Returns the current InsightInput when the host wants the in-session
   * surface to run. Absence is a feature: hosts that only want hub
   * curation can omit it and the insight proposer is skipped without
   * error.
   */
  readonly insightInputProvider?: () => InsightInput | null;
  readonly insightProposer?: InsightProposer;
  readonly hubProposer?: HubProposer;
}

export type CurateAnyProposal = InsightProposal | HubProposal;

export interface CurateResult {
  readonly subcommand: "curate";
  readonly status: "ok" | "reset-declined";
  readonly proposals: readonly CurateAnyProposal[];
  readonly skipped: {
    readonly insight: boolean;
    readonly hub: boolean;
  };
  readonly nextActions: readonly string[];
}

interface CurateArgs {
  insightOnly?: boolean;
  hubOnly?: boolean;
  resetDeclined?: boolean;
}

function parseCurateArgs(input: string): CurateArgs {
  const args: CurateArgs = {};
  for (const t of input.split(/\s+/).filter(Boolean)) {
    if (t === "--insight") args.insightOnly = true;
    else if (t === "--hub") args.hubOnly = true;
    else if (t === "--reset-declined") args.resetDeclined = true;
  }
  return args;
}

/**
 * Build the `/curate` command bound to a particular LLMJudge and optional
 * input provider. Returned as a factory so the registry can decide at
 * registration time whether to install the command at all.
 */
export function curateCommand(options: CurateOptions): Command<CurateResult> {
  const insightProposer = options.insightProposer ?? new InsightProposer({
    minTokensAccumulated: 2000,
    minTurnsBeforeFirstCheck: 6,
  });
  const hubProposer = options.hubProposer ?? new HubProposer({
    weakSignalAt: 3,
    strongSignalAt: 5,
  });

  return {
    name: "curate",
    description:
      "Run proactive-curator proposers and surface any proposals. Flags: --insight | --hub | --reset-declined.",
    args: [
      {
        name: "flag",
        description: "Optional: --insight, --hub, or --reset-declined.",
        required: false,
      },
    ],
    handler: async (input: CommandInput): Promise<CurateResult> => {
      const args = parseCurateArgs(input.rest);
      const { repoRoot } = input.context;
      const now = new Date();

      if (args.resetDeclined) {
        await resetDeclined(repoRoot);
        return {
          subcommand: "curate",
          status: "reset-declined",
          proposals: [],
          skipped: { insight: false, hub: false },
          nextActions: [
            "Decline list cleared. Both insight and hub proposers will re-evaluate previously declined topics on the next /curate.",
          ],
        };
      }

      const runInsight = !args.hubOnly;
      const runHub = !args.insightOnly;
      const declinedFingerprints = await loadDeclinedFingerprints(repoRoot, now);

      const proposals: CurateAnyProposal[] = [];
      let insightSkipped = !runInsight;
      let hubSkipped = !runHub;

      if (runInsight) {
        const insightInput = options.insightInputProvider?.() ?? null;
        if (!insightInput) {
          insightSkipped = true;
        } else {
          const p = await insightProposer.evaluate(insightInput, {
            cwd: repoRoot,
            declinedFingerprints,
            llm: options.llm,
            now,
          });
          if (p) proposals.push(p);
        }
      }

      if (runHub) {
        const p = await hubProposer.evaluate({}, {
          cwd: repoRoot,
          declinedFingerprints,
          llm: options.llm,
          now,
        });
        if (p) proposals.push(p);
      }

      const nextActions: string[] = [];
      if (proposals.length === 0) {
        nextActions.push("No proposals to surface right now.");
        if (insightSkipped && hubSkipped) {
          nextActions.push("Both proposers were skipped — run /curate without flags to enable both.");
        } else if (insightSkipped && runInsight) {
          nextActions.push("Insight proposer was skipped because no insightInputProvider was registered with this plugin.");
        }
      } else {
        nextActions.push(
          `${proposals.length} proposal${proposals.length === 1 ? "" : "s"} ready. Review each preview and call onAccept() or onDecline() to apply or dismiss.`,
        );
      }

      return {
        subcommand: "curate",
        status: "ok",
        proposals,
        skipped: { insight: insightSkipped, hub: hubSkipped },
        nextActions,
      };
    },
  };
}

export type { Proposal };
